import { useEffect, useState } from "react";
import Echo from "laravel-echo";
import Pusher, { Options as PusherOptions } from "pusher-js";
import Axios from "../utils/http-request";

interface AuthorizerChannel {
  name: string;
}

interface AuthorizerCallback {
  (error: boolean, data?: any): void;
}
// Extend Echo with a generic argument for the channel type (use `any` if unsure)
interface EchoInstance extends Echo<any> {}

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

const useEcho = (): EchoInstance | null => {
  const [echoInstance, setEchoInstance] = useState<EchoInstance | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Check if the required app key is available
      const appKey = process.env.REACT_APP_REVERB_APP_KEY;
      
      if (!appKey) {
        console.warn("REACT_APP_REVERB_APP_KEY is not set. Echo will not be initialized.");
        return;
      }

      // Ensure Pusher is available globally for Echo
      window.Pusher = Pusher;

      // Create Echo instance with configuration
      const echo = new Echo({
        broadcaster: "reverb",
        key: appKey,
        authorizer: (channel: AuthorizerChannel) => ({
          authorize: (socketId: string, callback: AuthorizerCallback) => {
            Axios.post("/broadcasting/auth", {
              socket_id: socketId,
              channel_name: channel.name,
            })
              .then((response) => callback(false, response.data))
              
              .catch((error) => {
                console.error("Error authorizing:", error); // Log error for debugging
                callback(true, error); // Pass error to the callback
              });
          },
        }),
        wsHost: process.env.REACT_APP_REVERB_HOST,
        wsPort: process.env.REACT_APP_REVERB_PORT,
        wssPort: process.env.REACT_APP_REVERB_PORT,
        forceTLS: (process.env.REACT_APP_REVERB_SCHEME ?? "https") === "https",
        enabledTransports: ["ws", "wss"],
      });
      
      setEchoInstance(echo as EchoInstance);
    }
  }, []);

  return echoInstance;
};

export default useEcho;
